/**
 * Claim Profile Page
 * Form for claiming an existing profile with verification
 * 
 * @author Varun Pandey
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { PhoneInput } from '../../components/ui/PhoneInput';
import { Select } from '../../components/ui/Select';
import { FileUpload, UploadedFile } from '../../components/ui/FileUpload';
import { AuthLayout } from '../../components/layout/AuthLayout';
import { Loader } from '../../components/ui/Loader';
import { SimilarProfile } from '../../components/features/SimilarProfiles';
import { AuthService, getAuthErrorMessage } from '../../services/authService';
import toast from 'react-hot-toast';
import { INDIAN_PHONE_ERROR_MESSAGE, isValidIndianPhoneNumber } from '../../utils/phone';

export const VERIFICATION_TYPES = ['PAN', 'AADHAAR', 'PASSPORT', 'DRIVING LICENSE'] as const;
export type VerificationType = (typeof VERIFICATION_TYPES)[number];

interface ClaimFormData {
  artist: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  managed_by: string;
  verification_type: VerificationType | '';
  verification_value: string;
  documents: UploadedFile[];
}

// Format Aadhaar as XXXX XXXX XXXX
const formatAadhaar = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 12);
  const parts = [];
  for (let i = 0; i < digits.length; i += 4) {
    parts.push(digits.slice(i, i + 4));
  }
  return parts.join(' ');
};

// Format PAN (uppercase, alphanumeric)
const formatPAN = (value: string): string => {
  return value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 10);
};

// Validate Aadhaar number (12 digits)
const validateAadhaar = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');
  return digits.length === 12;
};

// Validate PAN number (format: XXXXX0000X)
const validatePAN = (value: string): boolean => {
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return panRegex.test(value);
};

// Passport: alphanumeric, 6–12 chars typical
const validatePassport = (value: string): boolean => {
  const v = value.replace(/\s/g, '');
  return /^[A-Za-z0-9]{6,12}$/.test(v);
};

// Driving license: alphanumeric, reasonable length
const validateDrivingLicense = (value: string): boolean => {
  const v = value.replace(/\s/g, '');
  return v.length >= 8 && v.length <= 25 && /^[A-Za-z0-9\s\-/]+$/.test(v);
};

const verificationTypeOptions = VERIFICATION_TYPES.map((v) => ({
  value: v,
  label: v.replace(/_/g, ' '),
}));

const managedByOptions = [
  { value: 'self', label: 'Self' },
  { value: 'manager', label: 'Manager' },
  { value: 'agency', label: 'Agency' },
  { value: 'family', label: 'Family' },
];

export const ClaimProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const profile = location.state?.profile as SimilarProfile | undefined;
  const defaultArtist = profile?.slug ?? profile?.id ?? '';

  const [formData, setFormData] = useState<ClaimFormData>({
    artist: defaultArtist,
    name: '',
    email: '',
    phone: '',
    address: '',
    managed_by: '',
    verification_type: '',
    verification_value: '',
    documents: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const artistRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const addressRef = useRef<HTMLInputElement>(null);
  const verificationValueRef = useRef<HTMLInputElement>(null);

  // Sync default artist when profile is available
  useEffect(() => {
    if (defaultArtist && !formData.artist) {
      setFormData((prev) => ({ ...prev, artist: defaultArtist }));
    }
  }, [defaultArtist]);

  // Auto-focus first field
  useEffect(() => {
    if (artistRef.current) {
      setTimeout(() => artistRef.current?.focus(), 100);
    }
  }, []);

  // Focus first error field
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const errorFields: { key: string; ref: React.RefObject<HTMLInputElement | null> }[] = [
        { key: 'artist', ref: artistRef },
        { key: 'name', ref: nameRef },
        { key: 'email', ref: emailRef },
        { key: 'phone', ref: phoneRef },
        { key: 'address', ref: addressRef },
        { key: 'verification_value', ref: verificationValueRef },
      ];
      for (const field of errorFields) {
        if (errors[field.key] && field.ref.current) {
          setTimeout(() => field.ref.current?.focus(), 0);
          break;
        }
      }
    }
  }, [errors]);

  const updateFormData = (data: Partial<ClaimFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    const errorKeys = Object.keys(data);
    if (errorKeys.some((key) => errors[key])) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        errorKeys.forEach((key) => delete newErrors[key]);
        return newErrors;
      });
    }
  };

  const handleVerificationValueChange = (e: React.ChangeEvent<HTMLInputElement>, type: VerificationType | '') => {
    const raw = e.target.value;
    if (type === 'AADHAAR') {
      updateFormData({ verification_value: formatAadhaar(raw) });
    } else if (type === 'PAN') {
      updateFormData({ verification_value: formatPAN(raw) });
    } else {
      updateFormData({ verification_value: raw });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.artist.trim()) {
      newErrors.artist = 'Artist slug is required';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidIndianPhoneNumber(formData.phone)) {
      newErrors.phone = INDIAN_PHONE_ERROR_MESSAGE;
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.managed_by) {
      newErrors.managed_by = 'Please select an option';
    }

    if (!formData.verification_type) {
      newErrors.verification_type = 'Please select a verification type';
    } else {
      const v = formData.verification_value.replace(/\s/g, '');
      if (!v) {
        newErrors.verification_value = 'Verification value is required';
      } else if (formData.verification_type === 'AADHAAR' && !validateAadhaar(formData.verification_value)) {
        newErrors.verification_value = 'Please enter a valid 12-digit Aadhaar number';
      } else if (formData.verification_type === 'PAN' && !validatePAN(formData.verification_value)) {
        newErrors.verification_value = 'Please enter a valid PAN (e.g. ABCDE1234F)';
      } else if (formData.verification_type === 'PASSPORT' && !validatePassport(formData.verification_value)) {
        newErrors.verification_value = 'Please enter a valid 6–12 character passport number';
      } else if (formData.verification_type === 'DRIVING LICENSE' && !validateDrivingLicense(formData.verification_value)) {
        newErrors.verification_value = 'Please enter a valid driving license number';
      }
    }

    if (formData.documents.length < 1) {
      newErrors.documents = 'Please upload at least one document (max 3)';
    } else if (formData.documents.length > 3) {
      newErrors.documents = 'Maximum 3 document images allowed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      if (formData.phone && !isValidIndianPhoneNumber(formData.phone)) {
        toast.error(INDIAN_PHONE_ERROR_MESSAGE);
      }
      return;
    }

    setIsLoading(true);
    try {
      const fd = new FormData();
      fd.append('artist', formData.artist.trim());
      fd.append('name', formData.name.trim());
      fd.append('email', formData.email.trim());
      fd.append('phone', formData.phone.trim());
      fd.append('address', formData.address.trim());
      fd.append('managed_by', formData.managed_by);
      fd.append('verification_type', formData.verification_type);
      fd.append('verification_value', formData.verification_value.replace(/\s/g, ''));

      formData.documents.forEach((u) => {
        fd.append('images', u.file);
      });

      await AuthService.claimProfile(fd);
      setIsSuccess(true);
      toast.success('Claim submitted. We will review and get back to you.');
      setTimeout(() => {
        navigate('/login', { state: { message: 'Your claim has been submitted. We will review and get back to you.' } });
      }, 2000);
    } catch (error) {
      const message = getAuthErrorMessage(error, 'Failed to submit claim. Please try again.');
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Success State
  if (isSuccess) {
    return (
      <AuthLayout showImage={false}>
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 py-8 lg:px-0">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-500/20 mb-6">
            <CheckCircle className="h-10 w-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-white text-center mb-3">
            Claim Submitted Successfully!
          </h1>
          <p className="text-sm text-[#8E8E93] text-center max-w-sm">
            We've received your profile claim request. Our team will review the documents
            and get back to you within 2-3 business days.
          </p>
          <p className="text-xs text-[#8E8E93] mt-4">
            Redirecting to sign in...
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout showImage={false}>
      {isLoading && <Loader size="fullscreen" />}
      {/* Claim profile page: full viewport height, scrollable form area only */}
      <div
        className="claim-profile-page flex flex-col min-h-0 h-[100dvh] lg:h-screen lg:min-h-screen px-4 py-6 lg:px-0 lg:py-8"
        style={{ maxHeight: '100dvh' }}
      >
        {/* Fixed header – no scroll */}
        <div className="flex-shrink-0 space-y-4 space-x-6 pb-4">
          <div className="flex items-center gap-2">
            <button
              onClick={handleBack}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-[#2C2C2E] text-[#8E8E93] hover:bg-[#3A3A3C] hover:text-white transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          </div>
          <div className='flex flex-col gap-2'>
            <h1 className="text-2xl font-bold text-white">Claim your profile</h1>
            <p className="text-sm text-[#8E8E93] mt-1">
              Take ownership of your existing profile with a quick verification.
            </p>
          </div>

        </div>
        {profile && (
          <div className="flex items-center gap-3 p-4 rounded-lg bg-[#1C1C1E] border border-[#2C2C2E] mb-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#2C2C2E] text-lg font-semibold text-white flex-shrink-0">
              {profile.profileImage ? (
                <img
                  src={profile.profileImage}
                  alt={profile.stageName}
                  className="h-12 w-12 rounded-full object-cover"
                />
              ) : (
                profile.stageName.charAt(0).toUpperCase()
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{profile.stageName}</p>
              {profile.category && (
                <p className="text-xs text-[#8E8E93]">{profile.category}</p>
              )}
            </div>
            <span className="text-xs text-[#F33363] bg-[#F33363]/10 px-2 py-1 rounded">
              Claiming
            </span>
          </div>
        )}

        {/* Scrollable form + footer */}
        <div
          data-ptr-root
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden -mx-4 px-4 lg:mx-0 lg:px-0"
        >
          <div className="space-y-6 pb-8">
            {/* Form */}
            <form
              className="space-y-4"
              onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
            >
              {/* <Input
            ref={artistRef}
            label="Artist (slug)"
            placeholder="e.g. john-doe"
            value={formData.artist}
            onChange={(e) => updateFormData({ artist: e.target.value })}
            error={errors.artist}
          /> */}

              <Input
                ref={nameRef}
                label="Name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => updateFormData({ name: e.target.value })}
                error={errors.name}
              />

              <Input
                ref={emailRef}
                label="Email Address"
                type="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={(e) => updateFormData({ email: e.target.value })}
                error={errors.email}
              />

              <PhoneInput
                ref={phoneRef}
                label="Phone (10 digits)"
                value={formData.phone}
                onChange={(value) => updateFormData({ phone: value })}
                error={errors.phone}
              />

              <Input
                ref={addressRef}
                label="Address"
                placeholder="Enter your address"
                value={formData.address}
                onChange={(e) => updateFormData({ address: e.target.value })}
                error={errors.address}
              />

              <Select
                label="Profile Managed By"
                options={managedByOptions}
                value={formData.managed_by}
                onChange={(value) => updateFormData({ managed_by: value })}
                placeholder="Select"
                error={errors.managed_by}
              />

              <div className="flex flex-row justify-center items-center gap-2">
                <Select
                  label="Verification Type"
                  options={verificationTypeOptions}
                  value={formData.verification_type}
                  onChange={(value) =>
                    updateFormData({
                      verification_type: value as VerificationType | '',
                      verification_value: '',
                    })
                  }
                  placeholder="Select verification type"
                  error={errors.verification_type}

                />

                {formData.verification_type && (
                  <div className='w-full' style={{ animation: 'fadeSlideIn 0.3s ease-out' }}>
                    <Input
                      ref={verificationValueRef}
                      label={
                        formData.verification_type === 'AADHAAR'
                          ? 'Aadhaar Number'
                          : formData.verification_type === 'PAN'
                            ? 'PAN Number'
                            : formData.verification_type === 'PASSPORT'
                              ? 'Passport Number'
                              : 'Driving License Number'
                      }
                      placeholder={
                        formData.verification_type === 'AADHAAR'
                          ? 'XXXX XXXX XXXX'
                          : formData.verification_type === 'PAN'
                            ? 'ABCDE1234F'
                            : 'Enter number'
                      }
                      value={formData.verification_value}
                      onChange={(e) =>
                        handleVerificationValueChange(e, formData.verification_type as VerificationType)
                      }
                      error={errors.verification_value}
                      maxLength={formData.verification_type === 'AADHAAR' ? 14 : formData.verification_type === 'PAN' ? 10 : 25}
                      className='w-full'
                    />
                  </div>
                )}
              </div>


              <FileUpload
                label="Document images (1–3)"
                description="Upload 1 to 3 ID or verification document images"
                files={formData.documents}
                onChange={(files) => updateFormData({ documents: files })}
                error={errors.documents}
                maxFiles={3}
              />

              <Button
                type="submit"
                variant="active"
                className="w-full mt-6"
                isLoading={isLoading}
              >
                Claim Your Profile
              </Button>
            </form>

            <p className="text-xs text-[#8E8E93] text-center">
              By submitting, you agree to our verification process.
              We'll review your documents within 2-3 business days.
            </p>
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </AuthLayout>
  );
};
